import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { Button } from "../components/ui/button";

export default function ProductDetail() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const { user } = useAuth();
  const { add } = useCart();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    api.get(`/products/${id}`).then(({ data }) => setP(data)).catch(() => setP(null));
  }, [id]);

  if (!p) return <div className="py-24 text-center text-[#767676]">{t("loading")}</div>;

  const name = lang === "ar" ? p.name_ar : p.name_en;
  const desc = lang === "ar" ? p.desc_ar : p.desc_en;
  const hasOffer = !!p.offer && p.offer > 0;

  const handleAdd = async () => {
    if (!user) {
      toast.info(t("signIn"));
      navigate(`/login?redirect=/product/${id}`);
      return;
    }
    try {
      await add(p.id, qty);
      toast.success(lang === "ar" ? "أضيف إلى السلة" : "Added to cart");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  return (
    <main data-testid="product-detail" className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-16">
      <div className="grid md:grid-cols-2 gap-10 md:gap-16">
        <div className="aspect-[4/5] rounded-md overflow-hidden bg-[#F3F0EB]">
          <img src={p.image} alt={name} className="h-full w-full object-cover" />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#7B5CBF]">{p.brand}</div>
          <h1 className="font-display text-3xl md:text-4xl mt-2 leading-tight">{name}</h1>

          <div className="mt-6 flex items-baseline gap-3">
            {hasOffer ? (
              <>
                <span className="text-2xl font-medium">${p.final_price.toFixed(2)}</span>
                <span className="text-[#767676] line-through">${p.price.toFixed(2)}</span>
                <span className="text-xs bg-[#7B5CBF] text-white px-2 py-0.5 rounded-full">
                  -{Math.round(p.offer)}%
                </span>
              </>
            ) : (
              <span className="text-2xl font-medium">${p.price.toFixed(2)}</span>
            )}
          </div>

          <div className="mt-8">
            <div className="text-xs uppercase tracking-widest text-[#767676] mb-2">
              {t("description")}
            </div>
            <p className="text-[#1A1738] leading-relaxed">{desc}</p>
          </div>

          <div className="mt-10 flex items-center gap-3">
            <div className="flex items-center border border-[#E5E1D8] rounded-full">
              <button
                data-testid="qty-decr"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="w-10 h-11 text-lg"
              >−</button>
              <span className="w-8 text-center" data-testid="qty-value">{qty}</span>
              <button
                data-testid="qty-incr"
                onClick={() => setQty((q) => q + 1)}
                className="w-10 h-11 text-lg"
              >+</button>
            </div>
            <Button
              data-testid="add-to-cart"
              onClick={handleAdd}
              disabled={!p.active}
              className="rounded-full bg-[#1A1738] hover:bg-[#2C2650] text-white h-11 px-8"
            >
              {p.active ? t("addToCart") : t("outOfStock")}
            </Button>
          </div>
        </div>
      </div>
    </main>
  );
}
